import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiAlert, mdiCreditCard, mdiCalendar, mdiClock, mdiMapMarker, mdiAccount, mdiLogin, mdiPartyPopper } from '@mdi/js';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrencyFromMinor } from '../utils/currency';
import { useMobileMenu } from '../hooks/useMobileMenu';
import StoreBanner from '../components/store-banner';
import { bookingApi } from '../api/booking';
import { accountApi } from '../api/account';
import { authApi } from '../api/auth';
import GuestLoginModal from '../components/GuestLoginModal';
import EmailNameReconcileModal from '../components/EmailNameReconcileModal';
import GuestAccountOptIn from '../components/GuestAccountOptIn';
import useGuestAccountCreation from '../hooks/useGuestAccountCreation';
import BaseModal from '../components/BaseModal';
import TipModal from '../components/TipModal';
import danceLocaleIcon from '../assets/dance_locale_3-200h.png';
import './BookingConfirmation.css';
import './instructor/InstructorProfileSetup.css';
import './header.css';
import './Common.css';
import './Dashboard.css';

/**
 * Booking Confirmation Page - MVP Trust-Based Payment Model
 *
 * Displays:
 * - Instructor summary
 * - Booking details with contiguous session grouping
 * - Student info form (email required, phone optional)
 * - Optional message to instructor
 * - Price breakdown
 * - Approval required notice
 * - Request Booking button → Creates booking directly (no payment required)
 *
 * Payment Model (MVP):
 * - Creates booking with paymentStatus: "pending"
 * - Student pays instructor directly after approval (cash, bank transfer, payment app)
 * - Stripe Connect payment integration deferred to Phase 6
 *
 * Note: Email is required to match mobile app and industry standards
 * (Ticketmaster, Eventbrite, Airbnb all require email for confirmation)
 */
const BookingConfirmation = () => {
  const { instructorId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  useMobileMenu();

  // Get booking data from router state (passed from InstructorPublicProfile)
  const bookingData = location.state;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Student form state
  // Persist guest form state across navigation (e.g. Stripe error → Go Back).
  // sessionStorage clears on tab close; restores form rather than forcing re-entry.
  const FORM_STORAGE_KEY = 'bookingConfirmation.guestForm.v1';
  const persisted = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(FORM_STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  })();

  const [studentFirstName, setStudentFirstName] = useState(persisted.firstName || '');
  const [studentLastName, setStudentLastName] = useState(persisted.lastName || '');
  const [studentEmail, setStudentEmail] = useState(persisted.email || '');
  const [studentPhone, setStudentPhone] = useState(persisted.phone || '');
  const [studentMessage, setStudentMessage] = useState(persisted.message || '');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  // Guest prepay disclosure (PRP trust-signal-rethink E2E #5): guests always pay upfront,
  // whatever the instructor's payment timing. Surface that explicitly before a guest commits,
  // with a "contact the instructor instead" alternative. Shown once, then acknowledged.
  const [showGuestPrepayModal, setShowGuestPrepayModal] = useState(false);
  // Demo mode (mascot instructor): the end-of-demo card and the gift modal it opens.
  const [showDemoDone, setShowDemoDone] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [guestPrepayAcknowledged, setGuestPrepayAcknowledged] = useState(false);
  const [reconcileModal, setReconcileModal] = useState(null); // { storedName, typedName } | null
  // overrideStoredName: when true, the typed name overwrites the matched guest-shadow
  // profile's name on this booking. Sticky for the session — once the user picks
  // "Use new name" we keep that choice through to reservation submit.
  const [overrideStoredName, setOverrideStoredName] = useState(false);
  // Fingerprint of the last email+name combo that completed an on-file check.
  // Used to avoid re-prompting on submit when the user already answered.
  const [emailCheckedFor, setEmailCheckedFor] = useState('');
  // True once the entered email is confirmed to belong to an existing account —
  // hides the create-account opt-in (that email logs in instead).
  const [emailHasAccount, setEmailHasAccount] = useState(false);

  // Inline "create a free account" opt-in (shared with the ticket flow).
  const guestAccount = useGuestAccountCreation();

  // Core email-on-file check. Three branches:
  //   - hasAccount → registered user; show GuestLoginModal (don't reveal stored name)
  //   - hasGuestProfile + name differs → show EmailNameReconcileModal, return "needs choice"
  //   - otherwise → no modal
  // Returns true when a modal was shown (caller should abort whatever it was about to do).
  // Skipped entirely for authenticated users.
  const runEmailCheck = useCallback(async (emailValue, firstName, lastName) => {
    if (user) return false;
    if (!emailValue || !emailValue.includes('@') || !emailValue.includes('.')) return false;
    const fingerprint = `${emailValue.toLowerCase()}|${firstName.trim()}|${lastName.trim()}`;
    if (fingerprint === emailCheckedFor) return false; // already answered for this exact combo

    try {
      const instructorIdForCheck = bookingData?.instructorId || instructorId;
      const { hasAccount, hasGuestProfile, storedName } = await authApi.checkEmail(emailValue, instructorIdForCheck);
      setEmailCheckedFor(fingerprint);

      if (hasAccount) {
        setEmailHasAccount(true);
        guestAccount.setWantsAccount(false);
        setShowLoginModal(true);
        return true;
      }
      // No account on file — the create-account opt-in is valid for this email.
      setEmailHasAccount(false);
      if (hasGuestProfile && storedName) {
        const typedFirst = firstName.trim();
        const typedLast = lastName.trim();
        const namesDiffer =
          typedFirst && typedLast &&
          (typedFirst !== storedName.firstName || typedLast !== storedName.lastName);
        if (namesDiffer) {
          setReconcileModal({
            storedName,
            typedName: { firstName: typedFirst, lastName: typedLast },
          });
          return true;
        }
        setOverrideStoredName(false); // names match — silent dedup is correct
      }
      return false;
    } catch (_) {
      // Silently ignore — never block the booking form on a check failure
      return false;
    }
  }, [user, bookingData, instructorId, emailCheckedFor, guestAccount]);

  // Tab/click-out path — proactive UX so the modal appears as soon as the user
  // commits the email field, before they reach the submit button.
  const handleEmailBlur = useCallback((emailValue) => {
    runEmailCheck(emailValue, studentFirstName, studentLastName);
  }, [runEmailCheck, studentFirstName, studentLastName]);

  const handleReconcileChoice = (useNewName) => {
    setOverrideStoredName(useNewName);
    if (!useNewName && reconcileModal?.storedName) {
      // User chose to keep the name on file — sync the form so what they see
      // on the next page (and the receipt email) matches the actual booking name.
      setStudentFirstName(reconcileModal.storedName.firstName);
      setStudentLastName(reconcileModal.storedName.lastName);
      // Re-fingerprint to the synced name so the upcoming submit doesn't re-prompt.
      setEmailCheckedFor(`${studentEmail.toLowerCase()}|${reconcileModal.storedName.firstName}|${reconcileModal.storedName.lastName}`);
    }
    setReconcileModal(null);
  };

  // Pre-populate form when user is authenticated
  useEffect(() => {
    if (!user) return;
    accountApi.getProfile().then(profile => {
      if (profile.firstName) setStudentFirstName(profile.firstName);
      if (profile.lastName) setStudentLastName(profile.lastName);
      if (user.email) setStudentEmail(user.email);
    }).catch(() => {
      // If profile fetch fails, fall back to email from JWT only
      if (user.email) setStudentEmail(user.email);
    });
  }, [user]);

  // Persist GUEST form state to sessionStorage on every change so Stripe-error
  // → Go Back doesn't lose the user's typed details. Only for guests: a logged-in
  // user is always prefilled from their profile, so caching their details would
  // leak their name/email to the next guest in the same tab on a shared device
  // (PRP trust-signal-rethink E2E #4 — privacy fix).
  useEffect(() => {
    if (user) return; // never cache a logged-in user's details
    try {
      sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify({
        firstName: studentFirstName,
        lastName: studentLastName,
        email: studentEmail,
        phone: studentPhone,
        message: studentMessage,
      }));
    } catch {
      // sessionStorage may be unavailable (private mode); silently ignore
    }
  }, [user, studentFirstName, studentLastName, studentEmail, studentPhone, studentMessage]);

  // If no booking data, redirect back
  if (!bookingData) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>No booking data found</h2>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const {
    instructorName,
    date,
    dateDisplay,
    slots,
    hourlyRate,
    currency,
    location: instructorLocation,
    travelPeriod,
    paymentTiming = 'after-lesson',
    // Mascot (demo) instructor: walk through the page, book nothing (the backend refuses
    // mascot bookings anyway), and end on an offer to give a gift.
    isDemo = false,
    requiresApproval = true,
    approvalMode = 'existing-students'
  } = bookingData;

  // For authenticated users: before-lesson = pay via Stripe, after-lesson = pay directly
  const authRequiresPayment = !!user && paymentTiming === 'before-lesson';

  // Format time for display (24hr to 12hr with am/pm)
  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes}${ampm}`;
  };

  // Check if slots are contiguous (for session grouping display)
  const areContiguous = (slot1, slot2) => {
    return slot1.endTime === slot2.startTime;
  };

  // Group contiguous slots into sessions
  const groupedSessions = slots.reduce((sessions, slot) => {
    if (sessions.length === 0) {
      return [[slot]];
    }

    const lastSession = sessions[sessions.length - 1];
    const lastSlot = lastSession[lastSession.length - 1];

    if (areContiguous(lastSlot, slot)) {
      lastSession.push(slot);
    } else {
      sessions.push([slot]);
    }

    return sessions;
  }, []);

  // Check if there are multiple non-contiguous sessions
  const hasMultipleSessions = groupedSessions.length > 1;

  // Helper functions for session calculations
  const calculateTotalHours = (slots) => {
    const totalMinutes = slots.reduce((sum, slot) => sum + slot.duration, 0);
    return (totalMinutes / 60).toFixed(1);
  };

  const calculateSessionHours = (session) => {
    const totalMinutes = session.reduce((sum, slot) => sum + slot.duration, 0);
    return (totalMinutes / 60).toFixed(1);
  };

  const calculateSessionTotal = (session) => {
    const totalMinutes = session.reduce((sum, slot) => sum + slot.duration, 0);
    const hours = totalMinutes / 60;
    return (hourlyRate * hours).toFixed(2);
  };

  const totalHours = calculateTotalHours(slots);
  const artistFee = hourlyRate * parseFloat(totalHours);

  // Venue hire is only added to the displayed subtotal when the instructor's chosen
  // location has a known fixed/hourly fee in the SAME currency as the booking.
  // Cross-currency cases are settled server-side via fee converter at payment time.
  const venueHireKnown = !!(
    instructorLocation?.venueHire?.enabled &&
    Number(instructorLocation.venueHire.amount) > 0 &&
    (instructorLocation.venueHire.currency || currency) === currency
  );
  // venueHire.amount is venue-config in DOLLARS; convert to cents so this booking
  // line is homogeneous with artistFee (hourlyRate is now cents).
  const venueHireAmount = venueHireKnown
    ? (instructorLocation.venueHire.type === 'hourly'
        ? Math.round(Number(instructorLocation.venueHire.amount) * 100) * parseFloat(totalHours)
        : Math.round(Number(instructorLocation.venueHire.amount) * 100))
    : 0;

  const totalPrice = artistFee.toFixed(2);
  const estimatedSubtotal = (artistFee + venueHireAmount).toFixed(2);

  // (Removed getContactPlaceholder and getContactLabel - no longer needed)

  // Handle form submission - Create booking with "pay later" model
  const handleRequestBooking = async (skipGuestPrepay = false) => {
    // Validate required fields
    if (!studentFirstName.trim() || !studentLastName.trim()) {
      setFormError('Please enter your first and last name');
      return;
    }

    if (!studentEmail.trim()) {
      setFormError('Please enter your email address');
      return;
    }

    // Email validation
    if (!studentEmail.includes('@') || !studentEmail.includes('.')) {
      setFormError('Please enter a valid email address');
      return;
    }

    // Guest prepay disclosure (PRP trust-signal-rethink E2E #5): before a guest commits,
    // make the prepay requirement explicit (with a contact-instructor alternative). Once
    // acknowledged in the modal, the next call skips straight through.
    if (!user && !guestPrepayAcknowledged && !skipGuestPrepay) {
      setShowGuestPrepayModal(true);
      return;
    }

    // If the guest opted into creating an account, the password must be valid
    // before we do anything else.
    if (!user && guestAccount.wantsAccount && !guestAccount.validatePassword()) {
      return;
    }

    // Clear any previous errors
    setFormError('');
    setIsSubmitting(true);

    try {
      // Final email-on-file check before reservation. Catches the no-tab path
      // where the user types email and clicks Continue directly — runEmailCheck
      // will pop the appropriate modal and we abort. The user clicks Continue
      // again after answering, fingerprint matches, check is skipped, submit proceeds.
      if (!user) {
        const blocked = await runEmailCheck(studentEmail, studentFirstName, studentLastName);
        if (blocked) {
          setIsSubmitting(false);
          return;
        }
      }

      // If opted in (and the email is now confirmed new — runEmailCheck above did
      // not block), create the account + log in FIRST, then take the authenticated
      // booking path so the booking is owned by the new account from creation.
      // isAuthenticated won't flip synchronously this render, so track it locally.
      let bookedAsAuthed = !!user;
      if (!user && guestAccount.wantsAccount) {
        const created = await guestAccount.createAccountIfRequested({
          firstName: studentFirstName,
          lastName: studentLastName,
          email: studentEmail,
        });
        if (!created) {
          setIsSubmitting(false);
          return; // hook set its own password error
        }
        bookedAsAuthed = true;
      }

      const timeSlotData = {
        startTime: slots[0].startTime,
        endTime: slots[slots.length - 1].endTime
      };
      const studentInfoData = {
        firstName: studentFirstName.trim(),
        lastName: studentLastName.trim(),
        email: studentEmail.trim(),
        phone: studentPhone.trim() || '',
        message: studentMessage.trim() || ''
      };

      if (!bookedAsAuthed) {
        // Guest path: reserve slot, redirect to payment page
        let reserveData;
        try {
          reserveData = await bookingApi.reserveSlot({
            instructorId,
            bookingDate: date,
            timeSlot: timeSlotData,
            studentInfo: studentInfoData,
            overrideStoredName,
          });
        } catch (err) {
          setFormError(err.message || 'Failed to reserve slot. Please try again.');
          setIsSubmitting(false);
          return;
        }

        // Don't clear form state yet — keep it so a Stripe-error → Go Back returns the user
        // to a populated form. Cleared only on /booking-success.
        navigate('/booking-payment-guest', {
          state: {
            reservationId: reserveData.reservation.reservationId,
            expiresAt: reserveData.reservation.expiresAt,
            bookingPreview: reserveData.reservation.bookingPreview
          }
        });
        return;
      }

      // Authenticated path: create booking directly
      let booking;
      try {
        booking = await bookingApi.createBooking({
          instructorId,
          bookingDate: date,
          timeSlot: timeSlotData,
          studentInfo: studentInfoData
        });
      } catch (err) {
        setFormError(err.message || 'Failed to create booking. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Check if before-lesson payment is required
      if (booking.requiresPayment) {
        navigate('/booking-payment-guest', {
          state: {
            bookingId: booking.id,
            bookingPreview: {
              instructorName,
              instructorId,
              dateDisplay,
              timeSlot: timeSlotData,
              studentInfo: { firstName: studentFirstName.trim(), lastName: studentLastName.trim(), email: studentEmail.trim() },
              pricing: booking.pricing
            }
          }
        });
        return;
      }

      // After-lesson: render confirmed-vs-pending messaging from the RESPONSE status
      // (trust ladder — existing students auto-confirm, newcomers are held). Never
      // predict from settings. (PRP booking-confirmation-trust-signal-rethink)
      try { sessionStorage.removeItem(FORM_STORAGE_KEY); } catch { /* ignore */ }
      navigate('/booking-success', {
        state: {
          bookingId: booking.id,
          bookingStatus: booking.status,
          instructorName,
          dateDisplay,
          // Prefills the create-account card's register link on the success page.
          // Only a guest sees that card, but the value is harmless when set for an
          // authed user, and reading it here keeps the state shape uniform.
          guestEmail: studentEmail.trim(),
          timeSlot: {
            startTime: slots[0].startTime,
            endTime: slots[slots.length - 1].endTime
          },
          currency,
          // Server-computed money view (cents) — the success page renders it verbatim
          // and never derives a fee itself.
          effectivePricing: booking.effectivePricing,
        }
      });
    } catch (err) {
      console.error('Booking creation error:', err);
      setFormError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Payment handlers removed - MVP uses trust-based "pay later" model
  // Payment integration (Stripe Connect) deferred to Phase 6

  return (
    <div className="dashboard-container org-console booking-confirmation-page">
      {/* Standard Navigation Header */}
      <div data-role="Header" className="home-header-container">
        <header className="home-header">
          <a href="/" className="home-logo1">
            <img alt="logo" src={danceLocaleIcon} className="home-image1" />
            <span className="nav-wordmark">Dance Localé</span>
          </a>
          <div className="home-menu1">
            <a href="/" className="home-text10" style={{ textDecoration: 'none' }}>
              Home
            </a>
            {user
              ? <>
                  {user.organisationId
                    ? <a href="/dashboard" className="home-text12" style={{ textDecoration: 'none' }}>Dashboard</a>
                    : <a href="/my-hub" className="home-text12" style={{ textDecoration: 'none' }}>My Hub</a>}
                  {user.organisationId && <a href="/uploads" className="home-text12" style={{ textDecoration: 'none' }}>Uploads</a>}
                  <button className="home-text12" onClick={handleLogout} style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit' }}>Logout</button>
                </>
              : <a href="/login" className="home-text12" style={{ textDecoration: 'none' }}>Login</a>
            }
            <a href="/contact" className="home-text13" style={{ textDecoration: 'none' }}>
              Contact
            </a>
          </div>
          <div className="home-container11">
            <StoreBanner />
          </div>
          <div data-role="BurgerMenu" className="home-burger-menu">
            <svg viewBox="0 0 1024 1024" className="home-menu2">
              <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
              <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
              <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
            </svg>
          </div>
        </header>
        <div data-role="MobileMenu" className="home-mobile-menu">
          <div className="home-top">
            <div className="home-logo2">
              <img alt="logo" src={danceLocaleIcon} className="home-image2" />
            </div>
            <div data-role="CloseMobileMenu" className="home-close-menu">
              <svg viewBox="0 0 1024 1024" className="home-icon15">
                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
              </svg>
            </div>
          </div>
          <div className="home-mid">
            <span className="home-text14" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              Home
            </span>
            {user && (user.organisationId ? (
              <span className="home-text16" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
                Dashboard
              </span>
            ) : (
              <span className="home-text16" onClick={() => navigate('/my-hub')} style={{ cursor: 'pointer' }}>
                My Hub
              </span>
            ))}
            {user?.organisationId && (
              <span className="home-text16" onClick={() => navigate('/uploads')} style={{ cursor: 'pointer' }}>
                Uploads
              </span>
            )}
            <span className="home-text17" onClick={handleLogout} style={{ cursor: 'pointer' }}>
              Logout
            </span>
            <span className="home-text17" onClick={() => navigate('/contact')} style={{ cursor: 'pointer' }}>
              Contact
            </span>
          </div>
        </div>
      </div>

      <div className="dashboard-main">
        <div className="dashboard-content-wrapper">
          {/* Sub-Portal Header with Back Button */}
          <div className="sub-portal-header-card" style={{ padding: '20px' }}>
            <div className="sub-portal-header-title-row">
              <button onClick={() => navigate(-1)} className="back-nav-btn">
                ← Back to Calendar
              </button>
              <h1 className="sub-portal-header-title">Confirm Your Booking</h1>
            </div>

            <p className="sub-portal-header-subtitle" style={{ margin: 0 }}>
              {isDemo
                ? 'Demo booking: this is how booking a lesson works. Nothing will be booked or charged.'
                : 'Review your lesson details and submit your booking request'}
            </p>
          </div>

          {/* Booking Details - Mobile-Inspired Session Grouping */}
          <div className="section-card">
            <h2 className="section-title-mobile" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon path={mdiCalendar} size={0.9} color="#451f78" />
              Booking Details
            </h2>

            {/* Instructor row */}
            <div className="detail-row-mobile">
              <Icon path={mdiAccount} size={0.9} color="var(--dl-color-purple-default)" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{instructorName}</div>
                <div style={{ fontSize: '13px', color: 'var(--dl-color-medium)' }}>{formatCurrencyFromMinor(hourlyRate, currency)}/hour</div>
              </div>
            </div>

            {/* Multi-Session Notice */}
            {hasMultipleSessions && (
              <div className="multi-session-notice-mobile">
                <Icon path={mdiAlert} size={0.9} color="#10bfbf" />
                <span>Your booking includes {groupedSessions.length} separate lessons with gaps between them.</span>
              </div>
            )}

            {/* Date Row */}
            <div className="detail-row-mobile">
              <Icon path={mdiCalendar} size={0.9} color="#451f78" />
              <span>{dateDisplay}</span>
            </div>

            {/* Travel Period (if applicable) */}
            {travelPeriod && (
              <div
                className="travel-banner"
                style={{
                  background: `${travelPeriod.color}1A`,
                  borderLeftColor: travelPeriod.color,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}
              >
                <Icon path={mdiAlert} size={0.9} style={{ color: travelPeriod.color, flexShrink: 0 }} />
                <div>
                  <strong>NOTE:</strong> This is a booking at the following travel location:
                  <div>{travelPeriod.destination}</div>
                  {travelPeriod.timezone && (
                    <div className="travel-timezone">
                      {travelPeriod.timezone} {travelPeriod.timezoneOffset && `(UTC${travelPeriod.timezoneOffset})`}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Session Cards with "Lesson 1", "Lesson 2" Headers */}
            {groupedSessions.map((session, sessionIndex) => (
              <div key={sessionIndex} className="session-card-mobile">
                {groupedSessions.length > 1 && (
                  <div className="session-header">Lesson {sessionIndex + 1}</div>
                )}

                {session.map((slot, slotIndex) => (
                  <div key={slotIndex} className="time-slot-row">
                    <Icon path={mdiClock} size={0.8} color="#6e6969" />
                    <span className="time-range">
                      {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                    </span>
                    <span className="duration">({slot.duration} minutes)</span>
                  </div>
                ))}

              </div>
            ))}

            {/* Location Row */}
            <div className="detail-row-mobile">
              <Icon path={mdiMapMarker} size={0.9} color="#451f78" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{instructorLocation.description}</div>
                {instructorLocation.address && (
                  <div className="location-address">{instructorLocation.address}</div>
                )}
              </div>
            </div>
          </div>

        {/* Student Info Form */}
        <div className="section-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={{ margin: 0 }}>Your Information</h2>
            {!user && (
              <button
                className="booking-login-btn"
                onClick={() => setShowLoginModal(true)}
              >
                <Icon path={mdiLogin} size={0.75} />
                Log in to pre-fill
              </button>
            )}
          </div>

          <div className="booking-contact-row">
            <div className="form-group">
              <label htmlFor="studentFirstName" className="form-label">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="studentFirstName"
                value={studentFirstName}
                onChange={(e) => setStudentFirstName(e.target.value)}
                placeholder="First name"
                className="form-input"
                autoComplete="given-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="studentLastName" className="form-label">
                Last Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="studentLastName"
                value={studentLastName}
                onChange={(e) => setStudentLastName(e.target.value)}
                placeholder="Last name"
                className="form-input"
                autoComplete="family-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="studentEmail" className="form-label">
                Email Address <span className="required">*</span>
              </label>
              <input
                type="email"
                id="studentEmail"
                value={studentEmail}
                onChange={(e) => setStudentEmail(e.target.value)}
                onBlur={(e) => handleEmailBlur(e.target.value)}
                placeholder="your.email@example.com"
                className="form-input"
              />
            </div>
          </div>

          {/* Inline create-account opt-in — sits directly under the required
              name/email row (before the optional phone/message fields). Only for
              guests, hidden once the email is a known account (that email logs in
              instead). The account makes future bookings smoother + puts this
              lesson in the app. */}
          {!user && (
            <GuestAccountOptIn
              visible={!emailHasAccount}
              label="Create a free account to manage this booking, message your instructor, and rebook in the app"
              checked={guestAccount.wantsAccount}
              onChange={(e) => {
                guestAccount.setWantsAccount(e.target.checked);
                if (!e.target.checked) {
                  guestAccount.setPassword('');
                  guestAccount.setPasswordError('');
                }
              }}
              password={guestAccount.password}
              onPasswordChange={guestAccount.setPassword}
              passwordError={guestAccount.passwordError}
            />
          )}

          <div className="booking-optional-row">
            <div className="form-group">
              <label htmlFor="studentPhone" className="form-label">
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                id="studentPhone"
                value={studentPhone}
                onChange={(e) => setStudentPhone(e.target.value)}
                placeholder="+61 4XX XXX XXX"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="studentMessage" className="form-label">
                Message to Instructor (Optional)
              </label>
              <textarea
                id="studentMessage"
                value={studentMessage}
                onChange={(e) => setStudentMessage(e.target.value)}
                placeholder="e.g., I'm a beginner and would like to focus on basic steps..."
                className="form-textarea"
                rows="2"
              />
            </div>
          </div>

          {formError && (
            <div className="form-error">
              <Icon path={mdiAlert} size={0.8} />
              {formError}
            </div>
          )}
        </div>

        {/* Price Summary - Mobile-Inspired Breakdown */}
        <div className="section-card">
          <h2 className="section-title-mobile">Price Summary</h2>

          <div className="price-breakdown">
            {/* Artist Fee */}
            <div className="price-row">
              <span className="price-label">Artist Fee</span>
              <span className="price-amount">
                {formatCurrencyFromMinor(hourlyRate, currency)}/hour × {totalHours}h
              </span>
            </div>

            <div className="price-row">
              <span className="price-label"></span>
              <span className="price-amount price-amount-subtotal">
                {formatCurrencyFromMinor(totalPrice, currency)}
              </span>
            </div>

            {/* Venue Hire — show resolved venue hire when the instructor's chosen location
                has a fixed or hourly fee. TBA when currency differs (settled server-side)
                or the location has no fee enabled. */}
            <div className="price-row">
              <span className="price-label">Venue Hire</span>
              {venueHireKnown ? (
                <span className="price-amount">
                  {formatCurrencyFromMinor(venueHireAmount, currency)}
                  {instructorLocation.venueHire.type === 'hourly' ? ` (${Number(instructorLocation.venueHire.amount).toFixed(2)}/hour × ${totalHours}h)` : ''}
                </span>
              ) : (
                <span className="price-amount price-amount-tba">TBA*</span>
              )}
            </div>

            {/* Platform Fee - TBA */}
            <div className="price-row">
              <span className="price-label">Service Fee</span>
              <span className="price-amount price-amount-tba">TBA*</span>
            </div>

            {/* Divider */}
            <div className="price-divider"></div>

            {/* Total Row */}
            <div className="price-total-row">
              <span className="price-label">Estimated Total:</span>
              <span className="price-total">
                {formatCurrencyFromMinor(estimatedSubtotal, currency)}+
              </span>
            </div>
          </div>

          <p className="price-card-footnote">
            * Final platform fee will be added at payment; venue hire is shown above when known.
          </p>
        </div>

          {/* Approval Notice - authenticated + instructor may hold pay-later bookings.
              Copy depends on the approval mode (PRP booking-confirmation-trust-signal-rethink):
              existing-students softens the message since existing students are auto-confirmed. */}
          {user && requiresApproval && !isDemo && (
            <div className="approval-notice">
              <Icon path={mdiAlert} size={1} />
              <div>
                {approvalMode === 'existing-students' ? (
                  <>
                    <strong>Approval Required</strong>
                    <p>As you're new to this instructor, your booking request will be sent for their approval. You'll be notified once they respond.</p>
                  </>
                ) : (
                  <>
                    <strong>Approval Required</strong>
                    <p>Your booking request will be sent to the instructor for approval. You'll be notified once they respond.</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Request Booking / Continue to Payment — label depends on payment timing,
              not auth state. before-lesson always pays first; after-lesson is request-only. */}
          {isDemo ? (
            <button onClick={() => setShowDemoDone(true)} className="btn-request-booking">
              Finish demo
            </button>
          ) : (
          <button
            onClick={() => handleRequestBooking()}
            className="btn-request-booking"
            disabled={!studentFirstName.trim() || !studentLastName.trim() || !studentEmail.trim() || isSubmitting}
          >
            {isSubmitting
              ? (paymentTiming === 'before-lesson' ? 'Reserving...' : 'Requesting Booking...')
              : (paymentTiming === 'before-lesson' ? 'Continue to Payment' : 'Request Booking')}
          </button>
          )}

          {/* Payment Notice */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#f0f7ff',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#666',
            textAlign: 'center'
          }}>
            <Icon path={mdiCreditCard} size={0.7} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            {!user && (
              <><strong>Secure Payment:</strong> guest bookings are paid upfront via Stripe and confirmed instantly</>
            )}
            {user && authRequiresPayment && (
              <><strong>Secure Payment:</strong> You'll be redirected to pay via Stripe to confirm your booking</>
            )}
            {user && !authRequiresPayment && (
              <><strong>Payment:</strong> Pay now via the link we'll email you, or after the lesson</>
            )}
          </div>
        </div>
      </div>

      <BaseModal
        isOpen={showDemoDone}
        onClose={() => setShowDemoDone(false)}
        title="That's how booking works"
        icon={mdiPartyPopper}
        maxWidth={440}
        footer={
          <>
            <button className="base-modal-cancel-btn" onClick={() => navigate('/instructors')}>Find instructors</button>
            <button
              className="base-modal-save-btn"
              onClick={() => {
                setShowDemoDone(false);
                setShowTipModal(true);
              }}
            >
              Give a gift
            </button>
          </>
        }
      >
        <p>With a real instructor, Request Booking sends them your request and you're notified when they confirm.</p>
        <p>Love this app? Want to help out with a gift?</p>
      </BaseModal>

      <TipModal
        isOpen={showTipModal}
        onClose={() => setShowTipModal(false)}
        instructorId={instructorId}
        currency={currency}
        isAuthenticated={!!user}
      />

      <GuestLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => setShowLoginModal(false)}
      />

      <BaseModal
        isOpen={showGuestPrepayModal}
        onClose={() => setShowGuestPrepayModal(false)}
        title="Prepayment required to book"
        icon={mdiCreditCard}
        footer={
          <>
            <button className="base-modal-cancel-btn" onClick={() => setShowGuestPrepayModal(false)}>
              Cancel
            </button>
            <button
              className="base-modal-save-btn"
              onClick={() => {
                setGuestPrepayAcknowledged(true);
                setShowGuestPrepayModal(false);
                handleRequestBooking(true);
              }}>
              Continue to Payment
            </button>
          </>
        }
      >
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          This instructor requires guest bookings to be prepaid. You'll pay securely via Stripe
          and your booking is confirmed instantly.
        </p>
        <p style={{ marginBottom: 0, lineHeight: 1.5 }}>
          If you'd rather not pre-pay, contact the instructor directly to arrange your lesson.
        </p>
      </BaseModal>

      <EmailNameReconcileModal
        isOpen={!!reconcileModal}
        onClose={() => setReconcileModal(null)}
        storedName={reconcileModal?.storedName}
        typedName={reconcileModal?.typedName}
        onChoose={handleReconcileChoice}
      />
    </div>
  );
};

export default BookingConfirmation;
